import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Table,
} from "antd";
import type { TableProps } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "../../style/AEnquiry.css";
import { RootState } from "../../redux/store/store";
import { getAttachmentEnquiryRequest } from "../../redux/actions/attachmentEnquiryAction";
import { COPY_CELL } from "../../utils/copyGuard";

dayjs.extend(isSameOrAfter);
interface DataType {
  Id: string;
  Date: string;
  Name: string;
  Number: string;
  Address: string;
  City: string;
  Enquiry_Type: string;
  License: string;
  Registration: string;
  Model: string;
  Make_Year: string;
  Status: string;
  Remarks: string;
  Attended_By: string;
}

const PRIMARY = "rgb(137,67,157)";

const { RangePicker } = DatePicker;

const AEnquiry: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());

  const disableFutureDates = (current: dayjs.Dayjs) =>
    current.isAfter(dayjs(), "day");
  const dispatch = useDispatch();
  const { list, getLoading } = useSelector(
    (state: RootState) => state.attachmentEnquiry
  );
  const fetchData = () => {
    dispatch(
      getAttachmentEnquiryRequest({
        cityid: "",
        fromdate: fromDate.format("YYYY-MM-DD"),
        todate: toDate.add(1, "day").format("YYYY-MM-DD"),
        type: "",
        status: "",
      })
    );
  };
  const lastFetchedRangeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fromDate || !toDate) return;
    const rangeKey = `${fromDate.valueOf()}|${toDate.valueOf()}`;
    if (lastFetchedRangeRef.current === rangeKey) return;
    lastFetchedRangeRef.current = rangeKey;
    fetchData();
  }, [fromDate, toDate]);
  const handleFromDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setFromDate(date);
      if (toDate && toDate.isBefore(date)) {
        setToDate(date);
      }
    }
  };
  const handleToDateChange = (date: dayjs.Dayjs | null) => {
    if (date && fromDate && date.isSameOrAfter(fromDate)) {
      setToDate(date);
    }
  };
  const filteredData = (list || []).filter(
    (item: any) =>
      item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      String(item.enquiry_id ?? "").toLowerCase().includes(searchText.toLowerCase())
  );
  const columns: TableProps<DataType>["columns"] = [
    { title: "ID", dataIndex: "enquiry_id", key: "enquiry_id", className: COPY_CELL, align: "center", width: 90 },
    {
      title: "Date",
      dataIndex: "created",
      key: "created",
      align: "center",
      width: 120,
      render: (created: string) =>
        created ? dayjs(created).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: COPY_CELL,
      align: "center",
      width: 160,
    },
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      className: COPY_CELL,
      align: "center",
      width: 130,
    },
    {
      title: "Address",
      dataIndex: "area",
      key: "area",
      align: "center",
      width: 130,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      width: 130,
    },

    {
      title: "Enquiry Type",
      dataIndex: "enquiry_type",
      key: "enquiry_type",
      align: "center",
      width: 150,
    },

    {
      title: "License",
      dataIndex: "license",
      key: "license",
      align: "center",
      width: 200,
    },
    {
      title: "Registration",
      dataIndex: "registration",
      key: "registration",
      align: "center",
      width: 140,
    },

    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      align: "center",
      width: 130,
    },
    {
      title: "Make Year",
      dataIndex: "year",
      key: "year",
      align: "center",
      width: 120,
    },
    // {
    //   title: "Status",
    //   dataIndex: "Status",
    //   key: "Status",
    //   align: "center",
    //   width: 120,
    // },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      align: "center",
      width: 200,
    },

    {
      title: "Attended By",
      dataIndex: "attended_by",
      key: "attended_by",
      align: "center",
      width: 150,
    },
  ];

  return (
    <div className="fit-page fit-page--stack" style={{ background: "#f6f3f8", width: "100%", maxWidth: "100%" }}>
      <Card
        className="premium-card"
        styles={{ body: { padding: 12 } }}
        style={{ flex: "0 0 auto", marginBottom: 12, backgroundColor: "#c6a3cf" }}
      >
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col xs={24} md={13} lg={10}>
            <RangePicker
              size="large"
              format="DD-MM-YYYY"
              value={fromDate && toDate ? [fromDate, toDate] : null}
              disabledDate={disableFutureDates}
              style={{ width: "100%", maxWidth: 340 }}
              onChange={(dates) => {
                if (dates) {
                  handleFromDateChange(dates[0]);
                  handleToDateChange(dates[1]);
                }
              }}
            />
          </Col>

          <Col
            xs={24}
            md={11}
            lg={8}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Input
              size="large"
              prefix={<SearchOutlined />}
              placeholder="Search by Name or ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: "100%", maxWidth: 260, borderRadius: 10 }}
            />
          </Col>
        </Row>
      </Card>

      <div className="fit-table" style={{ minWidth: 0, width: "100%" }}>
        <Table
          bordered
          className="attachment-enquiry-table"
          columns={columns}
          rowKey={(record: any) => record.enquiry_id || record.Id}
          dataSource={filteredData}
          loading={getLoading}
          scroll={{ x: "max-content", y: 240 }}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "100"],
            showSizeChanger: true,
            showTotal: (count) => `Total ${count} enquiries`,
          }}
          style={{ borderRadius: 18 }}
          components={{
            header: {
              cell: ({ style, ...props }: any) => (
                <th
                  {...props}
                  style={{
                    ...style,
                    background: PRIMARY,
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: 600,
                  }}
                />
              ),
            },
          }}
        />
      </div>
    </div>
  );
};

export default AEnquiry;
